import type { ReactNode, RefObject } from 'react';
import { BookOpen, Keyboard, LifeBuoy, PlayCircle } from 'lucide-react';
import { Dialog } from '@paige/ui';
import { SUPPORT_EMAIL, SUPPORT_MAILTO, SUPPORT_NAME } from '../../support-contact';
import { HelpRow, type HelpRowProps } from './HelpRow';
import type { HelpChoices } from './help-choices';
import './help.css';

/** Help's four rows, in order. */
export function helpRows(choices: HelpChoices): (HelpRowProps & { key: string })[] {
  return [
    {
      key: 'keys',
      icon: <Keyboard size={20} />,
      title: 'Keyboard shortcuts',
      line: 'Every key on one sheet, or press F1.',
      action: { press: choices.shortcuts },
    },
    {
      key: 'tour',
      icon: <PlayCircle size={20} />,
      title: 'Take the tour',
      line: choices.tourLine,
      action: { press: choices.tour },
    },
    {
      key: 'docs',
      icon: <BookOpen size={20} />,
      title: 'Developer docs',
      line: 'Send documents and read results with the API.',
      action: { href: '/developers', external: true },
    },
    {
      key: 'support',
      icon: <LifeBuoy size={20} />,
      title: `Contact ${SUPPORT_NAME}`,
      line: `${SUPPORT_EMAIL}. A real person answers.`,
      action: { href: SUPPORT_MAILTO },
    },
  ];
}

/**
 * Help (gap list C8; Dustin, 2026-10-20): from the profile menu and the workbench's account door, the one way to
 * the keys, the tour, the developer docs and Ademero support. Visual, not wordy: an icon and one line each. There
 * is no "What's new": Paige has no changelog for customers (the developer docs keep the API's).
 */
export function HelpSheet({
  choices,
  onClose,
  returnFocusTo,
}: {
  choices: HelpChoices;
  onClose: () => void;
  returnFocusTo: RefObject<HTMLElement | null>;
}): ReactNode {
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      variant="drawer"
      size="sm"
      title="Help"
      returnFocusTo={returnFocusTo}
    >
      <nav className="help-rows" aria-label="Help">
        {helpRows(choices).map(({ key, ...row }) => (
          <HelpRow key={key} {...row} />
        ))}
      </nav>
    </Dialog>
  );
}
